import React, { Suspense, FC, Fragment, useState, useRef, useEffect } from 'react';
import { compact, cloneDeep, isEqual } from 'lodash';
import { MutationResult } from 'react-apollo';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { Form, Formik, FormikProps } from 'formik';
import { findIndex, differenceBy } from 'lodash';
import { useLocation } from 'react-router';

import {
  ContentInput,
  ManualPostMutation,
  Network,
  PostType,
  SocialAccount,
  useManualPostMutation,
} from 'api';
import { FormikEffect } from 'components';
import { ContentProvider, SelectedNetworksProvider } from 'providers';
import { contentCollection } from 'apollo/reducers';
import { ShareDialog } from 'features/sharing';

import { formatBody, formatDisclosures } from '../helpers';
import { PostSchema } from '../validations';
import { PostForm } from '../types';
import { usePrevious, useQueryParams, useSocialConnections } from 'hooks';
import { FACEBOOK_ID } from '../constants';

const CreatePostForm: FC<PostForm> = props => {
  const {
    groupIds,
    toggleWork,
    handleCompleted,
    renderEditor,
    children,
    handleError,
    isChanging,
  } = props;

  const [open, setOpen] = useState(false);
  const [content, setContent] = useState(null);
  const connections = useSocialConnections();
  const [selectedNetworks, setSelectedNetworks] = useState(getSelectedNetworks());
  const previousConnections = usePrevious(connections);

  const hasMountRan = useRef(false);
  const formikRef = useRef(null);
  const { query, variables } = useQueryParams();
  const [processContent] = useManualPostMutation({
    onError: handleError,
    onCompleted,
  });
  const location = useLocation();

  useEffect(() => {
    if (!hasMountRan.current) {
      hasMountRan.current = true;
      return;
    }

    const [addedNetwork] = differenceBy(connections, previousConnections, 'id');
    const nextNetworks = cloneDeep(selectedNetworks);

    nextNetworks.splice(0, 0, addedNetwork);
    setSelectedNetworks(nextNetworks);
  }, [connections]);

  function getLeadingAccount(filteredConnections: SocialAccount[], provider: Network) {
    return filteredConnections.find(conn => conn.provider === provider);
  }

  function getSelectedNetworks() {
    const filterExpired = connections.filter(connection => !connection.expires_soon);
    const twitterAccount = getLeadingAccount(filterExpired, Network.Twitter);
    const linkedinAccount = getLeadingAccount(filterExpired, Network.Linkedin);
    const initialSelectedNetworks: Array<SocialAccount | string> = compact(
      [twitterAccount].concat(linkedinAccount),
    );

    return initialSelectedNetworks.concat(FACEBOOK_ID);
  }

  function getValues(): ContentInput {
    const searchParams = new URLSearchParams(location.search);
    const shareUrl = searchParams.get('share-url');
    return {
      group_ids: groupIds,
      content_source_id: 'manual',
      is_engageable: false,
      is_important: false,
      is_shareable: true,
      is_pinned: false,
      pinned_end_at: null,
      network_ids: [],
      lock_commentary: false,
      content_object: {
        post_type: PostType.Manual,
        body: shareUrl || '',
        url: shareUrl,
      },
      commentary: [
        { network: Network.Twitter, commentary: '' },
        { network: Network.Facebook, commentary: '' },
        { network: Network.Linkedin, commentary: '' },
      ],
      url_param_values: [],
      disclosures: [
        { network: Network.Facebook, disclosure_id: '' },
        { network: Network.Twitter, disclosure_id: '' },
        { network: Network.Linkedin, disclosure_id: '' },
      ],
    };
  }

  function addPost(memoryCache: InMemoryCache, result: MutationResult) {
    contentCollection({
      name: 'addPost',
      cache: memoryCache,
      result,
      query,
      variables,
    });

    formikRef.current.resetForm();
  }

  function toggleNetwork(account: SocialAccount | string) {
    const nextNetworks = cloneDeep(selectedNetworks);
    const index = nextNetworks.findIndex(selectedNetwork => isEqual(selectedNetwork, account));

    // Remove network
    if (index >= 0) {
      nextNetworks.splice(index, 1);
      return setSelectedNetworks(nextNetworks);
    }

    // Add Facebook
    if (typeof account === 'string') {
      nextNetworks.splice(0, 0, account);
      return setSelectedNetworks(nextNetworks);
    }

    // Add or toggle accounts for a given network
    const existingNetworkIndex = findIndex(nextNetworks, ['provider', account.provider]);
    if (existingNetworkIndex >= 0) {
      nextNetworks.splice(existingNetworkIndex, 1);
    }

    nextNetworks.splice(0, 0, account);
    setSelectedNetworks(nextNetworks);
  }

  function handleFormChange(values: ContentInput) {
    if (!values.is_shareable) {
      setSelectedNetworks([]);
    }
  }

  async function handleSubmit(contentInput: ContentInput) {
    const contentToSubmit: ContentInput = cloneDeep(contentInput);

    contentToSubmit.group_ids = compact(contentToSubmit.group_ids);
    contentToSubmit.content_object.body = formatBody(contentToSubmit);
    contentToSubmit.commentary = contentToSubmit.commentary.filter(c => !!c.commentary);
    contentToSubmit.disclosures = formatDisclosures(contentToSubmit);

    delete contentToSubmit.content_object.url_metadata;

    // @ts-ignore
    delete contentToSubmit.content_object.tweet_metadata;
    // @ts-ignore
    delete contentToSubmit.content_object.youtube_metadata;
    // @ts-ignore
    delete contentToSubmit.content_object.instagram_metadata;
    // @ts-ignore
    delete contentToSubmit.content_object.reddit_metadata;
    // @ts-ignore
    delete contentToSubmit.content_object.metadata_keys;

    toggleWork();

    const options = {
      update: addPost,
      variables: { content: contentToSubmit },
    };

    return processContent(options);
  }

  function onCompleted(mutationResult: ManualPostMutation) {
    const initiateShare = !!selectedNetworks.length;

    if (!initiateShare) {
      return handleCompleted(mutationResult, {
        i18nKey: 'posting:created',
        showNotification: true,
      });
    }

    setContent(mutationResult.processContent);
    setOpen(true);
    handleCompleted(mutationResult, { i18nKey: 'posting:created', showNotification: false });
  }

  // We need props to reset the form
  function renderStoreProps(formikProps: FormikProps<ContentInput>): null {
    formikRef.current = formikProps;
    return null;
  }

  function renderSuspense(formikProps: FormikProps<ContentInput>) {
    return <Suspense fallback={renderChildren()}>{renderEditor(formikProps)}</Suspense>;
  }

  function renderChildren() {
    return (
      <Fragment>
        {children}
        {content && (
          <ContentProvider value={{ content }}>
            <ShareDialog onClose={() => setOpen(false)} open={open} />
          </ContentProvider>
        )}
      </Fragment>
    );
  }

  return (
    <Formik
      enableReinitialize
      onSubmit={handleSubmit}
      initialValues={getValues()}
      validationSchema={PostSchema}>
      {formikProps => (
        <Form className="createPostForm">
          {renderStoreProps(formikProps)}
          <FormikEffect onChange={handleFormChange} delay={0} validate={false} />
          <SelectedNetworksProvider value={{ selectedNetworks, toggleNetwork }}>
            {isChanging ? renderSuspense(formikProps) : renderChildren()}
          </SelectedNetworksProvider>
        </Form>
      )}
    </Formik>
  );
};

export default CreatePostForm;
